import { ArrowDropDown } from "@mui/icons-material";
import Comments from "@mui/icons-material/Comment";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
} from "@mui/material";
import { red } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { get, includes } from "lodash";
import React, { useState } from "react";
import KMSwitch from "../../../common/js/components/KMSwitch";
import SaveAndExitButton from "../../../common/js/components/SaveAndExitButton";
import ExecuteInvoiceModal from "../invoice/ExecuteInvoiceModal";
import constants from "./constants";

const PageToolbar = styled("div")(() => ({
    padding: "10px 0px",
    backgroundColor: "#fafafa",
    position: "sticky",
    top: "70px",
    margin: "0 0 20px 0",
    zIndex: 2,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "1vw",
}));

const ToolbarButtons = styled("div")(() => ({
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: "8px",
}));

const ProgressDiv = styled("div")(() => ({
    textAlign: "center",
    width: "5em",
    display: "inline-block",
}));

const SplitButtonContainer = styled("div")(() => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
    flexWrap: "nowrap",
}));

const BypassCompApprovalSwitch = styled(KMSwitch)(() => ({
    margin: 0,
    paddingRight: "2vw",
}));

const DeleteButton = styled(Button)(() => ({
    backgroundColor: red[500],
    color: "white",
    "&:hover": {
        backgroundColor: red[800],
    },
    marginRight: "0.5vw",
}));

const MenuSubItem = styled(MenuItem)({
    "&>a": {
        paddingLeft: "30px !important",
    },
});

const StyledCommentsButton = styled(Button)({
    color: "black",
});

const CompToolbar = (props) => {
    const {
        confirmDialog,
        deal,
        disableInputs,
        elementsSaving,
        executeInvoiceModalOpen,
        handleCheckboxClick,
        handleConfirmTerminateDeal,
        handleExecuteInvoice,
        handleToggleBooleanProperty,
        history,
        location,
        numComments,
        toggleComments,
        toggleConfirm,
        saveInsideComp,
        saveAndExitInsideComp,
        submitInsideComp,
        userProfile,
        handleStartReviewingAsCorporateData,
        toggleTerminateDialog,
        terminateDialog,
        confirmTerminateDialog,
        handleTerminateDeal,
        terminationDocument,
    } = props;

    const handleCancel = () => {
        toggleConfirm(null);
    };

    const handleSaveAndExitComp = async () => {
        const result = await saveAndExitInsideComp();
        if (result.success) {
            history.push("/deals");
        }
    };

    const handleDeleteRequest = async () => {
        const result = await handleTerminateDeal(deal.dealKey, terminationDocument.upload);
        if (result.success) {
            history.push("/deals");
        }
        toggleTerminateDialog(false);
    };

    const handleConfirmDelete = async () => {
        const result = await handleConfirmTerminateDeal(deal.dealKey, terminationDocument.upload, deal.dealStatus);
        toggleTerminateDialog(false);
        if (result.success) {
            history.push("/deals");
        }
    };

    const handleSubmitAndNavigate = async (submitURL) => {
        const result = await submitInsideComp(submitURL);
        if (result.success) {
            history.push("/deals");
        }
    };
    const brokerSubmitActions = {
        null: {
            buttonText: "Submit",
            submit: () => {
                handleSubmitAndNavigate(constants.urls.broker_submit(deal.dealKey));
            },
            variant: "contained",
        },
        Draft: {
            buttonText: "Submit",
            submit: () => {
                handleSubmitAndNavigate(constants.urls.broker_submit(deal.dealKey));
            },
            variant: "contained",
        },
        "Broker Action Required": {
            buttonText: "Submit",
            submit: () => {
                handleSubmitAndNavigate(constants.urls.broker_submit(deal.dealKey));
            },
            variant: "contained",
        },
    };
    const submitActions = {
        broker: {
            ...brokerSubmitActions,
        },
        evp: {
            ...brokerSubmitActions,
        },
        debtEquity: {
            ...brokerSubmitActions,
        },
        portlandBrokerAdmin: {
            ...brokerSubmitActions,
            "Portland Review Required": {
                buttonText: "Portland Review Required",
                submit: (callback) => {
                    handleSubmitAndNavigate(callback);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_comp_to_broker,
                    Approve: constants.urls.process_portland_approval,
                    "Revert to Draft": constants.urls.revert_comp_to_draft,
                },
            },
        },
        arizonaBrokerAdmin: {
            ...brokerSubmitActions,
            "AZ Review Required": {
                buttonText: "AZ Review Required",
                submit: (callback) => {
                    handleSubmitAndNavigate(callback);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_comp_to_broker,
                    Approve: constants.urls.process_arizona_approval,
                },
            },
        },
        dataEntry: {
            "Needs Review": {
                buttonText: "Start",
                submit: () => {
                    handleStartReviewingAsCorporateDataAndRedirect(deal, location.pathname);
                },
                enabled: true,
            },
            "In Review": {
                buttonText: "In Review",
                submit: (callback) => {
                    handleSubmitAndNavigate(callback);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_comp_to_broker,
                    "Final Review": constants.urls.approve_comp,
                },
            },
            "Admin Action Required": {
                buttonText: "In Review",
                submit: (callback) => {
                    handleSubmitAndNavigate(callback);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_comp_to_broker,
                    "Final Review": constants.urls.approve_comp,
                },
            },
        },
        dataAdmin: {
            "Final Review": {
                buttonText: "In Review",
                submit: (callback) => {
                    handleSubmitAndNavigate(callback);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_comp_to_broker,
                    "Admin Action Required": constants.urls.reassign_comp_to_data_entry,
                    Approve: constants.urls.approve_comp,
                },
            },
        },
    };

    const mainAction = get(submitActions, `${userProfile.role}.${deal.compStatus}`, null);
    const role = get(userProfile, "role", null);
    const compStatus = get(deal, "compStatus", null);

    const confirm = (url) => {
        toggleConfirm(url(deal.dealKey));
    };
    const handleStartReviewingAsCorporateDataAndRedirect = async (deal) => {
        const result = await handleStartReviewingAsCorporateData(deal, location.pathname);
        if (result) {
            window.location.reload();
        }
    };

    const [mainActionAnchorEl, setMainActionAnchorEl] = useState(null);
    const mainActionDropdown = Boolean(mainActionAnchorEl);

    const handleMainActionDropdown = (event) => {
        setMainActionAnchorEl(event.currentTarget);
    };

    const handleCloseMainActionDropdown = () => {
        setMainActionAnchorEl(null);
    };

    const handleSaveAndRedirect = async () => {
        await handleSaveAndExitComp();
    };

    return (
        <PageToolbar>
            <div>
                {executeInvoiceModalOpen && (
                    <ExecuteInvoiceModal
                        toggleModal={() => handleToggleBooleanProperty("executeInvoiceModalOpen")}
                        open={executeInvoiceModalOpen}
                        handleExecuteInvoice={() => handleExecuteInvoice()}
                        saving={get(elementsSaving, "invoice")}
                    />
                )}
                <h2 className="page-title">{deal.compStatus === "Draft" ? "Create Comp" : "Comp"}</h2>
                <p className="page-subtitle">
                    {deal.propertyType} {deal.transactionType}
                </p>
            </div>
            <div>
                <ToolbarButtons>
                    {includes(["dataAdmin", "dataEntry"], role) && (
                        <BypassCompApprovalSwitch
                            checked={deal.sendVouchersToAccountingFlg}
                            onChange={(e) => handleCheckboxClick("sendVouchersToAccountingFlg", e)}
                            value={deal.sendVouchersToAccountingFlg}
                            disabled={disableInputs}
                            label="Send Voucher to Accounting"
                        />
                    )}
                    <KMSwitch
                        checked={deal.confidentialFlg}
                        onChange={(e) => handleCheckboxClick("confidentialFlg", e)}
                        value={deal.confidentialFlg}
                        disabled={disableInputs}
                        style={{ margin: 0 }}
                        label="Confidential"
                    />

                    {deal.compStatus !== "Outside Draft" && (
                        <StyledCommentsButton disabled={Boolean(!deal.dealKey)} onClick={toggleComments}>
                            <Comments /> Comments ({numComments})
                        </StyledCommentsButton>
                    )}
                    {((includes(["portlandBrokerAdmin", "arizonaBrokerAdmin"], userProfile.role) &&
                        includes(
                            ["Portland Termination Review Required", "AZ Termination Review Required"],
                            compStatus
                        )) ||
                        (userProfile.role === "broker" &&
                            includes(["brokerActionRequired", "Draft"], compStatus) &&
                            includes(
                                ["OR", "AZ"],
                                get(deal, "listing.property.primaryAddress.city.state.stateCode")
                            ))) && (
                        <DeleteButton
                            variant="contained"
                            onClick={() => toggleTerminateDialog(true, deal.dealStatus)}
                            disabled={disableInputs}
                        >
                            Terminate
                        </DeleteButton>
                    )}
                    <SplitButtonContainer>
                        {elementsSaving.saveAndExit ? (
                            <ProgressDiv>
                                <CircularProgress size={20} color="primary" />
                            </ProgressDiv>
                        ) : (
                            <SaveAndExitButton
                                onSave={saveInsideComp}
                                onSaveAndExit={handleSaveAndRedirect}
                                disabled={disableInputs}
                            />
                        )}
                        {elementsSaving.executeInvoice ? (
                            <ProgressDiv>
                                <CircularProgress size={20} color="primary" />
                            </ProgressDiv>
                        ) : (
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => handleToggleBooleanProperty("executeInvoiceModalOpen")}
                            >
                                Generate Invoice
                            </Button>
                        )}
                        {mainAction && mainAction.options ? ( //if the action button had multiple options
                            elementsSaving.submit ? (
                                <ProgressDiv>
                                    <CircularProgress size={20} color="primary" />
                                </ProgressDiv>
                            ) : (
                                <React.Fragment>
                                    <Button
                                        id="Main-Action-Menu"
                                        aria-controls={mainActionDropdown ? "Main-Action-Menu" : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={mainActionDropdown ? "true" : undefined}
                                        onClick={handleMainActionDropdown}
                                        variant="contained"
                                        endIcon={<ArrowDropDown />}
                                    >
                                        {get(mainAction, "buttonText")}
                                    </Button>
                                    <Menu
                                        id="Main-Action-Menu"
                                        anchorEl={mainActionAnchorEl}
                                        open={mainActionDropdown}
                                        onClose={handleCloseMainActionDropdown}
                                        anchorOrigin={{
                                            vertical: "bottom",
                                            horizontal: "right",
                                        }}
                                        transformOrigin={{
                                            vertical: "top",
                                            horizontal: "right",
                                        }}
                                    >
                                        {Object.keys(get(mainAction, "options", {})).map((x) => {
                                            let url = get(mainAction, `options.${x}`);
                                            return (
                                                <MenuSubItem style={{ fontSize: "14px" }} onClick={() => confirm(url)}>
                                                    {x}
                                                </MenuSubItem>
                                            );
                                        })}
                                    </Menu>
                                </React.Fragment>
                            )
                        ) : mainAction !== null ? (
                            elementsSaving.submit ? (
                                <ProgressDiv>
                                    <CircularProgress size={20} color="primary" />
                                </ProgressDiv>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    disabled={mainAction.enabled ? false : disableInputs}
                                    onClick={() => toggleConfirm()}
                                >
                                    {mainAction.buttonText}
                                </Button>
                            )
                        ) : (
                            deal && (
                                <Button disabled variant="contained" color="secondary">
                                    {deal.compStatus}
                                </Button>
                            )
                        )}
                    </SplitButtonContainer>
                </ToolbarButtons>
            </div>
            <Dialog open={terminateDialog.open} maxWidth="xs" aria-labelledby="confirmation-dialog-title">
                <DialogTitle id="confirmation-dialog-title">Terminate Deal?</DialogTitle>
                <DialogContent>
                    Are you sure you want to terminate this Deal? If terminated, this Deal will be removed from the Deal
                    workflow, added to your Closed Deals for recording purposes, and you can no longer receive payment
                    for this deal.
                </DialogContent>
                <DialogContent>Please only select "Yes" if this Deal has been terminated by your client.</DialogContent>
                <DialogActions>
                    <Button onClick={() => toggleTerminateDialog(false, deal.dealStatus)} color="primary">
                        No
                    </Button>
                    <Button onClick={() => handleDeleteRequest()} color="primary">
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirmTerminateDialog.open} maxWidth="xs" aria-labelledby="confirmation-dialog-title">
                <DialogTitle id="confirmation-dialog-title">Terminate Deal?</DialogTitle>
                <DialogContent>
                    Are you sure you want to terminate this Deal? If terminated, this Deal will be removed from the Deal
                    workflow and added to the Broker's Closed Deals for recording purposes.
                </DialogContent>
                <DialogContent>Please only select "Yes" if this Deal has been terminated by your client.</DialogContent>
                <DialogActions>
                    <Button onClick={() => toggleTerminateDialog(false, deal.dealStatus)} color="primary">
                        No
                    </Button>
                    <Button onClick={() => handleConfirmDelete()} color="primary">
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirmDialog.open} maxWidth="xs" aria-labelledby="confirmation-dialog-title">
                <DialogTitle id="confirmation-dialog-title">Continue?</DialogTitle>
                <DialogContent>Are you sure you want to submit this status change?</DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => mainAction["submit"](confirmDialog.onSubmit)} color="primary">
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
        </PageToolbar>
    );
};

export default CompToolbar;
